import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { useShipping } from "@/context/ShippingContext";

const CartSheet = () => {
  const { items, updateQty, removeItem, subtotal, open, setOpen } = useCart();
  const { t, tl } = useLang();
  const { user } = useAuth();
  const { free_shipping_threshold, standard_shipping_cost, pickup_address } = useShipping();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fulfillmentMethod, setFulfillmentMethod] = useState("shipping");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const applyCoupon = async () => {
    setApplyingCoupon(true);
    try {
      const { data } = await api.post("/discount-codes/validate", { code: couponCode.trim() });
      setCoupon(data);
      toast.success("Codice sconto applicato");
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || "Impossibile verificare il codice");
    } finally { setApplyingCoupon(false); }
  };
  const digitalOnly = items.length > 0 && items.every((item) => item.lookup_key.startsWith("gift-card-"));
  const physicalSubtotal = items.filter((item) => !item.lookup_key.startsWith("gift-card-")).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const couponDiscount = coupon ? items.filter(i => !i.lookup_key.startsWith("gift-card-")).reduce((sum, i) => {
    const cents = Math.round(i.price * 100);
    return sum + (cents - Math.max(1, Math.round(cents * (100 - coupon.discount_percent) / 100))) * i.quantity / 100;
  }, 0) : 0;
  const discountedPhysicalSubtotal = physicalSubtotal - couponDiscount;
  const shippingCost = digitalOnly || fulfillmentMethod === "pickup" || discountedPhysicalSubtotal >= free_shipping_threshold ? 0 : standard_shipping_cost;

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: user ? user.email : undefined,
        fulfillment_method: fulfillmentMethod,
        discount_code: coupon?.code,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il pagamento. Riprova.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-sheet"
        className="w-full sm:max-w-md flex flex-col bg-bone border-l border-stone-200"
      >
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl tracking-tight text-charcoal text-left">
            {t("cart.title")}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-stone-400 gap-4">
            <ShoppingBag size={40} strokeWidth={1} />
            <p className="text-sm">{t("cart.empty")}</p>
            <Button
              variant="outline"
              className="rounded-none border-stone-300"
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
              data-testid="cart-empty-shop"
            >
              {t("cart.continue")}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-6 space-y-6">
              {items.map((i) => (
                <div key={i.lookup_key} className="flex gap-4" data-testid={`cart-item-${i.lookup_key}`}>
                  {i.image ? <img src={i.image} alt={tl(i.name)} className="w-20 h-24 object-cover bg-white" /> : <div className="flex w-20 h-24 shrink-0 items-center justify-center bg-charcoal text-white"><ShoppingBag size={24}/></div>}
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <div>
                        <p className="font-serif text-base text-charcoal leading-tight">{tl(i.name)}</p>
                        <p className="text-[11px] uppercase tracking-widest text-stone-400 mt-1">
                          {tl(i.format)}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(i.lookup_key)}
                        className="text-stone-400 hover:text-charcoal transition-colors"
                        data-testid={`cart-remove-${i.lookup_key}`}
                        aria-label={t("cart.remove")}
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-stone-300">
                        <button
                          className="px-2 py-1 text-stone-600 hover:text-charcoal"
                          onClick={() => updateQty(i.lookup_key, i.quantity - 1)}
                          data-testid={`cart-dec-${i.lookup_key}`}
                        >
                          <Minus size={13} />
                        </button>
                        <span className="px-3 text-sm">{i.quantity}</span>
                        <button
                          className="px-2 py-1 text-stone-600 hover:text-charcoal"
                          onClick={() => updateQty(i.lookup_key, i.quantity + 1)}
                          data-testid={`cart-inc-${i.lookup_key}`}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <span className="text-sm text-charcoal">{formatPrice(i.price * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-stone-200 pt-5 space-y-3">
              {!digitalOnly && <div className="space-y-2">
                <label htmlFor="cart-discount-code" className="text-xs text-stone-600">Codice sconto</label>
                <div className="flex gap-2"><input id="cart-discount-code" value={couponCode} onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCoupon(null); }} className="min-w-0 flex-1 border border-stone-300 bg-white px-3 py-2 text-sm" placeholder="Inserisci il codice" maxLength={40}/><button type="button" disabled={applyingCoupon || !couponCode.trim()} onClick={applyCoupon} className="border border-stone-300 px-3 text-xs disabled:opacity-50">{applyingCoupon ? "…" : "Applica"}</button></div>
                {coupon && <p className="flex justify-between text-xs text-stone-600"><span>{coupon.code}: −{coupon.discount_percent}%</span><button onClick={() => { setCoupon(null); setCouponCode(""); }} className="underline">Rimuovi</button></p>}
                <p className="text-[10px] text-stone-500">I codici riservati richiedono l’accesso al proprio account. Le Gift Card sono escluse.</p>
              </div>}
              {!digitalOnly && <fieldset className="space-y-2">
                <legend className="text-[11px] uppercase tracking-[0.16em] text-stone-500 mb-2">
                  Modalità di consegna
                </legend>
                <label className={`block border p-3 cursor-pointer transition-colors ${fulfillmentMethod === "shipping" ? "border-charcoal bg-white" : "border-stone-200"}`}>
                  <span className="flex items-start gap-3">
                    <input type="radio" name="fulfillment" checked={fulfillmentMethod === "shipping"} onChange={() => setFulfillmentMethod("shipping")} className="mt-1 accent-black" />
                    <span><span className="block text-sm text-charcoal">Spedizione</span><span className="block text-xs text-stone-500 mt-0.5">{discountedPhysicalSubtotal >= free_shipping_threshold ? `Gratuita: hai raggiunto ${formatPrice(free_shipping_threshold)}` : `${formatPrice(standard_shipping_cost)} · gratuita da ${formatPrice(free_shipping_threshold)}`}</span></span>
                  </span>
                </label>
                <label className={`block border p-3 cursor-pointer transition-colors ${fulfillmentMethod === "pickup" ? "border-charcoal bg-white" : "border-stone-200"}`}>
                  <span className="flex items-start gap-3">
                    <input type="radio" name="fulfillment" checked={fulfillmentMethod === "pickup"} onChange={() => setFulfillmentMethod("pickup")} className="mt-1 accent-black" />
                    <span><span className="block text-sm text-charcoal">Ritiro gratuito ad Avellino</span><span className="block text-xs text-stone-500 mt-0.5">{pickup_address}</span></span>
                  </span>
                </label>
              </fieldset>}
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">{t("cart.subtotal")}</span>
                <span className="text-charcoal" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">{digitalOnly ? "Consegna digitale via email" : fulfillmentMethod === "pickup" ? "Ritiro" : t("cart.shipping")}</span>
                <span className="text-champagne uppercase tracking-widest text-xs">{digitalOnly ? "Inclusa" : shippingCost === 0 ? t("cart.free") : formatPrice(shippingCost)}</span>
              </div>
              {!digitalOnly && fulfillmentMethod === "shipping" && shippingCost > 0 && <p className="text-[11px] text-stone-500">Aggiungi ancora {formatPrice(Math.max(0, free_shipping_threshold - discountedPhysicalSubtotal))} di prodotti per ottenere la spedizione gratuita.</p>}
              {coupon && <div className="flex justify-between text-sm"><span>Sconto {coupon.code}</span><span>−{formatPrice(couponDiscount)}</span></div>}
              <div className="flex justify-between border-t border-stone-200 pt-3 text-sm font-semibold"><span>Totale</span><span>{formatPrice(subtotal - couponDiscount + shippingCost)}</span></div>
              <Button
                onClick={checkout}
                disabled={loading}
                data-testid="checkout-button"
                className="w-full rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 text-xs uppercase tracking-[0.2em]"
              >
                {loading ? "..." : t("cart.checkout")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
